<script>
  import { onMount } from 'svelte'
  import { Input, Label } from 'sveltestrap'
  import LanguageDropdown from '../../common/LanguageDropdown.svelte'
  import { fade } from 'svelte/transition'
  import { DEFAULT_LOCALE } from '../../env/config'
  import { setupI18n } from '../../services/i18n'
  import { navigate } from 'svelte-routing'
  import { Link } from 'svelte-routing' // Import Link for navigation back to login

  // Default data for password recovery page
  const defaultData = {
    title: 'Recuperar contraseña',
    subtitle: 'Para acceder a Rastros.',
    logoURL: '/login',
    principalMessage:
      '¡Ingresa tu dirección de correo electrónico y recibirás las instrucciones para recuperar tu contraseña!',
    emailLabel: 'Email',
    emailPlaceholder: 'Ingrese usuario',
    sendButton: 'Enviar',
    sendButtonLink: '/',
    copyright: 'Enteleqqia SPA. Hegga SPA.',
    errorSendEmail:
      'Hubo un error con la solicitud. Por favor, intenta nuevamente.',
    errorUnvalidateEmail: 'Por favor, introduce un email válido.',
    errorEmptyEmail: 'El campo de email no puede estar vacío.',
    // Fields for password reset form (if token is present)
    newPasswordLabel: 'Nueva contraseña',
    newPasswordPlaceholder: 'Escribe tu nueva contraseña',
    confirmPasswordLabel: 'Confirmar contraseña',
    confirmPasswordPlaceholder: 'Escribe de nuevo tu contraseña',
    confirmButton: 'Cambiar contraseña',
    confirmButtonLink: '/login',
    emptyFieldError: 'El campo no puede ser vacío',
    noIqualMessage: 'Las contraseñas ingresadas no son iguales',
    logo: { url: '' }, // Set to empty string as it should load from API
    backToLoginLabel: '¿Recordaste tu contraseña? Iniciar Sesión',
    backToLoginLink: '/login', // Explicitly define the back to login link
    contactInfo: {
      address: 'Badajoz 100 - Las Condes, Santiago, Chile',
      phone: '+56 2 38035840',
      email: 'info@rastros.cl',
    },
  }

  let data = { ...defaultData }
  let email = '' // For email recovery form
  let newPassword = ''
  let confirmPassword = ''
  let errorMessage = ''
  let isLoading = false
  let availableLanguages = []
  let currentYear = new Date().getFullYear()
  let resetCode = '' // To store the reset token from URL
  let showEmailForm = true // Control which form to display

  // Load page data from API
  async function loadPageData() {
    try {
      isLoading = true
      errorMessage = '' // Clear previous errors
      let locale = localStorage.getItem('LANGUAGE') || DEFAULT_LOCALE
      if (locale === 'es') {
        locale = 'es-CL' // Ensure correct locale for API
      }
      console.log('Fetching recovery page data for locale:', locale)

      const response = await fetch(
        `https://api.rastros.cl/api/recovery-change-pass-page?populate=%2A&locale=${locale}`
      )
      console.log('API Response:', response)
      if (!response.ok) {
        const errorText = await response.text()
        console.error(
          'Failed to fetch recovery change password page data. Status:',
          response.status,
          'Response text:',
          errorText
        )
        throw new Error('Failed to fetch recovery change password page data')
      }
      const apiData = await response.json()
      console.log('API Data:', apiData)

      if (apiData.data) {
        // Merge attributes if they exist, otherwise merge data directly
        if (apiData.data.attributes) {
          data = { ...defaultData, ...apiData.data.attributes }
        } else {
          data = { ...defaultData, ...apiData.data } // Fallback if attributes not present
        }

        // Explicitly handle logo if present directly under apiData.data
        // The provided API response shows logo directly under 'data' not 'attributes'
        if (apiData.data.logo && apiData.data.logo.url) {
          data.logo = { url: apiData.data.logo.url }
        }

        // Explicitly handle localizations if present directly under apiData.data
        availableLanguages = []
        if (apiData.data.locale) {
          // Use apiData.data.locale for the main locale
          availableLanguages.push({
            label: apiData.data.locale,
            value: apiData.data.locale,
            flag: `/assets/images/flags/${apiData.data.locale.split('-')[0]}.jpg`,
          })
        }

        if (
          apiData.data.localizations &&
          Array.isArray(apiData.data.localizations)
        ) {
          apiData.data.localizations.forEach((loc) => {
            availableLanguages.push({
              label: loc.locale,
              value: loc.locale,
              flag: `/assets/images/flags/${loc.locale.split('-')[0]}.jpg`,
            })
          })
        }
        availableLanguages.sort((a, b) => a.label.localeCompare(b.label))
        console.log('Available languages:', availableLanguages)
      } else {
        console.error(
          'API response structure is not as expected: apiData.data is missing',
          apiData
        )
        throw new Error('Invalid API response structure')
      }
    } catch (error) {
      console.error('Error cargando datos de cambio de contraseña:', error)
      errorMessage =
        'Error al cargar la configuración. Por favor, recarga la página.'
    } finally {
      isLoading = false
    }
  }

  // Handle language change
  function handleLanguageChange(event) {
    const newLocale = event.detail // Assuming LanguageDropdown emits 'chageLang' with new locale in detail
    console.log('Language changed to:', newLocale)
    localStorage.setItem('LANGUAGE', newLocale)
    loadPageData() // Reload data for the new language
  }

  // Handle password change form submission
  async function handleSendRecoveryEmail() {
    if (!email) {
      errorMessage = data.errorEmptyEmail || defaultData.errorEmptyEmail
      return
    }
    // Basic email validation regex
    const emailRegex = new RegExp(
      '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
    )
    if (!emailRegex.test(email)) {
      errorMessage =
        data.errorUnvalidateEmail || defaultData.errorUnvalidateEmail
      return
    }

    try {
      isLoading = true
      errorMessage = ''

      // TODO: Replace with the actual API endpoint for sending recovery email
      const response = await fetch(
        'https://api.rastros.cl/api/auth/forgot-password',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        errorMessage =
          errorData.message || data.errorSendEmail || defaultData.errorSendEmail
        return
      }

      // On success, show a success message or navigate to a confirmation page
      errorMessage =
        'Se ha enviado un correo electrónico con instrucciones para restablecer tu contraseña.'
      // Optionally, navigate to a success page or back to login after a delay
      // navigate(data.sendButtonLink || '/login');
    } catch (error) {
      console.error('Error al enviar el correo de recuperación:', error)
      errorMessage = data.errorSendEmail || defaultData.errorSendEmail
    } finally {
      isLoading = false
    }
  }

  async function handleChangePassword() {
    if (!newPassword || !confirmPassword) {
      errorMessage = data.emptyFieldError || defaultData.emptyFieldError
      return
    }
    if (newPassword !== confirmPassword) {
      errorMessage = data.noIqualMessage || defaultData.noIqualMessage
      return
    }

    try {
      isLoading = true
      errorMessage = ''

      // Extract the 'code' (reset token) from the URL query parameters
      const urlParams = new URLSearchParams(window.location.search)
      resetCode = urlParams.get('code')

      if (!resetCode) {
        errorMessage = 'No se encontró el código de restablecimiento en la URL.'
        isLoading = false
        return
      }

      const response = await fetch(
        'https://api.rastros.cl/api/auth/reset-password',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: resetCode,
            password: newPassword,
            passwordConfirmation: confirmPassword,
          }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        errorMessage =
          errorData.message || 'Error al restablecer la contraseña.'
        return
      }

      // On success, navigate to login page
      navigate(data.confirmButtonLink || '/login')
    } catch (error) {
      console.error('Error al cambiar la contraseña:', error)
      errorMessage = 'Ocurrió un error al procesar tu solicitud.'
    } finally {
      isLoading = false
    }
  }

  // Initialize on component mount
  onMount(() => {
    const urlParams = new URLSearchParams(window.location.search)
    resetCode = urlParams.get('code')
    if (resetCode) {
      showEmailForm = false // If code is present, show password reset form
    } else {
      showEmailForm = true // Otherwise, show email recovery form
    }
    loadPageData()
  })
</script>

<div class="login-page-wrapper">
  <header class="login-header">
    <div class="header-logo">
      {#if isLoading}
        <div class="skeleton skeleton-logo"></div>
      {:else if data.logo?.url}
        <img src={data.logo.url} alt="Logo" class="img-fluid" />
      {:else}
        <!-- Fallback if no logo from API and not loading -->
        <img
          src="/src/assets/images/logo-enteleqqia-blanco.png"
          alt="Default Logo"
        />
      {/if}
    </div>
    <div class="header-right-content">
      {#if isLoading}
        <div class="skeleton skeleton-dropdown"></div>
      {:else}
        <LanguageDropdown
          on:chageLang={handleLanguageChange}
          {availableLanguages}
          selectedLang={localStorage.getItem('LANGUAGE') || DEFAULT_LOCALE}
        />
      {/if}
      <div class="contact-info">
        {#if isLoading}
          <div class="skeleton skeleton-text short"></div>
          <div class="skeleton skeleton-text short"></div>
        {:else}
          <span
            ><i class="mdi mdi-map-marker"></i>
            {data.contactInfo?.address || defaultData.contactInfo.address}</span
          >
          <span
            ><i class="mdi mdi-phone"></i>
            {data.contactInfo?.phone || defaultData.contactInfo.phone}</span
          >
        {/if}
      </div>
    </div>
  </header>

  <div class="login-container">
    <div class="left-panel">
      <!-- Background image handled by CSS -->
    </div>
    <div class="right-panel">
      <div class="login-main">
        <div class="login-card">
          <div class="auth-logo">
            <!-- This logo is hidden by CSS, header logo is used -->
            <img src={data.logo?.url || defaultData.logo.url} alt="Logo" />
          </div>
          <h4 class="login-title">{data.title || defaultData.title}</h4>
          <p class="text-muted text-center mb-4">
            {data.principalMessage || defaultData.principalMessage}
          </p>

          {#if errorMessage}
            <div class="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          {/if}

          {#if showEmailForm}
            <!-- Email Recovery Form -->
            <form on:submit|preventDefault={handleSendRecoveryEmail}>
              <div class="form-group">
                <Label for="email"
                  >{data.emailLabel || defaultData.emailLabel}</Label
                >
                <Input
                  type="email"
                  id="email"
                  placeholder={data.emailPlaceholder ||
                    defaultData.emailPlaceholder}
                  bind:value={email}
                  class="form-control"
                  required
                />
              </div>
              <div class="d-grid mt-4">
                <button
                  type="submit"
                  class="btn btn-primary"
                  disabled={isLoading}
                >
                  {#if isLoading}
                    <span
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Cargando...
                  {:else}
                    {data.sendButton || defaultData.sendButton}
                  {/if}
                </button>
              </div>
            </form>
          {:else}
            <!-- Password Reset Form -->
            <form on:submit|preventDefault={handleChangePassword}>
              <div class="form-group">
                <Label for="newpassword"
                  >{data.newPasswordLabel ||
                    defaultData.newPasswordLabel}</Label
                >
                <Input
                  type="password"
                  id="newpassword"
                  placeholder={data.newPasswordPlaceholder ||
                    defaultData.newPasswordPlaceholder}
                  bind:value={newPassword}
                  class="form-control"
                  required
                />
              </div>
              <div class="form-group">
                <Label for="confirmpassword"
                  >{data.confirmPasswordLabel ||
                    defaultData.confirmPasswordLabel}</Label
                >
                <Input
                  type="password"
                  id="confirmpassword"
                  placeholder={data.confirmPasswordPlaceholder ||
                    defaultData.confirmPasswordPlaceholder}
                  bind:value={confirmPassword}
                  class="form-control"
                  required
                />
              </div>
              <div class="d-grid mt-4">
                <button
                  type="submit"
                  class="btn btn-primary"
                  disabled={isLoading}
                  >{#if isLoading}
                    <span
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Cargando...
                  {:else}
                    {data.confirmButton || defaultData.confirmButton}
                  {/if}</button
                >
              </div>
            </form>
          {/if}

          <div class="mt-5 text-center">
            <p>
              <Link to={data.backToLoginLink || defaultData.backToLoginLink}
                >{data.backToLoginLabel || defaultData.backToLoginLabel}</Link
              >
            </p>
          </div>
        </div>
      </div>

      <footer class="login-footer">
        <p>
          © {currentYear}
          {data.copyright || defaultData.copyright}
        </p>
      </footer>
    </div>
  </div>
</div>

<style>
  .login-page-wrapper {
    display: flex;
    flex-direction: column; /* Stack header and main content vertically */
    min-height: 100vh;
    font-family: 'Arial', sans-serif;
  }

  .login-container {
    /* This now holds left and right panels */
    display: flex;
    flex: 1; /* Take remaining vertical space */
  }

  .left-panel {
    flex: 1;
    background-image: url('https://rastros.cl/assets/images/roadway.jpg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    position: relative;
    display: none; /* Hidden by default on small screens */
  }

  .right-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px;
    background-color: #f8f9fa; /* Light background for the form side */
  }

  .login-header {
    display: flex;
    justify-content: space-between; /* Logo left, contact/lang right */
    align-items: center;
    background-color: #333333; /* Dark header background */
    color: #ffffff; /* White text for dark header */
    padding: 15px 20px; /* Add some padding */

    flex-wrap: wrap; /* Allow wrapping on smaller screens */
  }

  .header-logo img {
    max-height: 100px; /* Adjust logo size in header */
    width: auto;
  }

  .header-right-content {
    display: flex;
    flex-direction: column; /* Stack contact info and language selector */
    align-items: flex-end; /* Align to the right */
  }

  .contact-info {
    text-align: right; /* Align text to the right */
  }

  .contact-info span {
    display: block;
    font-size: 0.85em;
    color: inherit; /* Inherit color from header */
    margin-bottom: 5px;
  }

  .contact-info i {
    margin-right: 5px;
    color: inherit; /* Inherit color from header */
  }

  .language-selector {
    margin-top: 5px; /* Smaller space from contact info */
  }

  /* Remove original auth-logo styles */
  .auth-logo {
    display: none; /* Hide the original logo container */
  }

  .login-main {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0;
  }

  .login-card {
    background: #fff;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 400px; /* Max width for the login form */
  }

  .auth-logo img {
    max-height: 80px; /* Adjust logo size */
    margin-bottom: 20px;
  }

  .login-title {
    text-align: center;
    color: #343a40;
    margin-bottom: 30px;
    font-size: 1.8em;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-control {
    border-radius: 5px;
    padding: 10px 15px;
    border: 1px solid #ced4da;
  }

  .form-control:focus {
    border-color: #405189;
    box-shadow: 0 0 0 0.2rem rgba(64, 81, 137, 0.25);
  }

  .input-group .form-control {
    border-right: none;
  }

  .input-group .btn {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-color: #ced4da;
  }

  .btn-primary {
    background-color: #405189;
    border-color: #405189;
    padding: 10px 20px;
    font-size: 1.1em;
    border-radius: 5px;
  }

  .btn-primary:hover {
    background-color: #364574;
    border-color: #364574;
  }

  .alert {
    margin-bottom: 20px;
    font-size: 0.9em;
    text-align: center;
  }

  .login-footer {
    text-align: center;
    padding: 20px;
    background-color: #f8f9fa;
    border-top: 1px solid #e9ecef;
    color: #6c757d;
  }

  /* Skeleton styles */
  .skeleton {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
  }

  .skeleton-text {
    height: 1em;
    margin-bottom: 0.5em;
  }

  .skeleton-text.short {
    width: 50%;
  }

  .skeleton-text.medium {
    width: 75%;
  }

  .skeleton-logo {
    width: 150px;
    height: 50px;
  }

  /* Responsive adjustments */
  @media (min-width: 768px) {
    .left-panel {
      display: block; /* Show left panel on medium screens and up */
    }

    .right-panel {
      flex: 0 0 50%; /* Right panel takes 50% width */
      max-width: 50%;
    }

    .login-header {
      display: flex;
      justify-content: space-between; /* Logo left, contact/lang right */
      align-items: center;
      background-color: #333333; /* Dark header background */
      color: #ffffff; /* White text for dark header */
      padding: 15px 20px; /* Add some padding */

      flex-wrap: wrap; /* Allow wrapping on smaller screens */
    }

    .header-logo img {
      max-height: 100px; /* Adjust logo size in header */
      width: auto;
    }

    .header-right-content {
      display: flex;
      flex-direction: column; /* Stack contact info and language selector */
      align-items: flex-end; /* Align to the right */
    }

    .language-selector {
      margin-top: 0;
    }
  }

  @media (min-width: 992px) {
    .right-panel {
      flex: 0 0 40%; /* Adjust width for larger desktops */
      max-width: 40%;
    }
  }

  @media (min-width: 1200px) {
    .right-panel {
      flex: 0 0 35%; /* Further adjust for extra large screens */
      max-width: 35%;
    }
  }
</style>
